// competitive-insight-rail.tsx
//
// The competitive insight rail: the glanceable teaser layer under Brand
// Monitoring's Recommendation ranking table. Deep Signal's Competitive tab is
// the destination it links into, not where it renders.
//
// This file is presentation only. Every string a card shows, and the order the
// cards appear in, comes from competitive-detectors — which is also where the
// copy rules live and where phase 2's backend findings will merge in.
//
// The rail deals five findings at a time. "Show me more" swaps in the next
// five rather than appending, so the row stays one row on desktop instead of
// growing a second, and the last view ends on a card that hands the reader to
// Deep Signal.
import { useState } from "react";
import { attributeLabel } from "@/app/services/brand/competitive-intelligence-api";
import type { RailFinding } from "./competitive-detectors";

export function CompetitiveInsightRail({ views, onSelect, onSeeAll, linkable = true }: {
  /** Ranked findings, already dealt into views of five. */
  views: RailFinding[][];
  onSelect: (attribute: string) => void;
  onSeeAll: () => void;
  /** False when the destination (Deep Signal) is not live for this viewer.
   *  The cards still render because the findings are measured either way, but
   *  without a click affordance, and the links are dropped. */
  linkable?: boolean;
}) {
  const [view, setView] = useState(0);
  if (!views.length) return null;

  const index = Math.min(view, views.length - 1);
  const cards = views[index];
  const lastView = index === views.length - 1;

  return (
    <div style={{ marginTop: "20px" }}>
      <div
        style={{
          display: "flex", gap: "12px", alignItems: "stretch",
          // Wide screens fit five; narrow ones scroll the rail rather than
          // reflowing it into a grid that no longer reads as one row.
          overflowX: "auto", paddingBottom: "4px",
        }}
      >
        {cards.map((f) => {
          const clickable = !!f.attribute && linkable;
          return (
            <button
              key={f.id}
              type="button"
              disabled={!clickable}
              onClick={() => f.attribute && onSelect(f.attribute)}
              title={clickable ? `Open ${attributeLabel(f.attribute as string)} in Deep Signal` : undefined}
              style={{
                flex: "1 1 0", minWidth: "224px", textAlign: "left",
                backgroundColor: "var(--card)", border: "1px solid var(--border)",
                borderRadius: "10px", padding: "14px 16px",
                cursor: clickable ? "pointer" : "default",
                font: "inherit", color: "inherit", display: "block",
              }}
              onMouseEnter={(e) => {
                if (clickable) e.currentTarget.style.backgroundColor = "var(--muted)";
              }}
              onMouseLeave={(e) => {
                if (clickable) e.currentTarget.style.backgroundColor = "var(--card)";
              }}
            >
              <div style={{
                fontSize: "11px", fontWeight: 600, letterSpacing: "0.4px",
                textTransform: "uppercase", color: "var(--muted-foreground)", marginBottom: "8px",
              }}>
                {f.eyebrow}
              </div>
              <div style={{
                fontSize: "22px", fontWeight: 700, lineHeight: 1.1,
                color: "var(--foreground)", fontVariantNumeric: "tabular-nums", marginBottom: "8px",
              }}>
                {f.stat}
              </div>
              <div style={{
                fontSize: "12.5px", lineHeight: 1.5, color: "var(--foreground)",
              }}>
                {f.headline}
              </div>
              {f.detail && (
                <div style={{
                  fontSize: "12px", lineHeight: 1.5, color: "var(--muted-foreground)", marginTop: "4px",
                }}>
                  {f.detail}
                </div>
              )}
            </button>
          );
        })}

        {/* The end of the pool: the reader is handed to the tool itself. */}
        {lastView && linkable && (
          <button
            type="button"
            onClick={onSeeAll}
            style={{
              flex: "1 1 0", minWidth: "224px", textAlign: "left",
              backgroundColor: "var(--muted)", border: "1px dashed var(--border)",
              borderRadius: "10px", padding: "14px 16px", cursor: "pointer",
              font: "inherit", color: "inherit", display: "block",
            }}
          >
            <div style={{
              fontSize: "12.5px", lineHeight: 1.5, color: "var(--foreground)", marginBottom: "8px",
            }}>
              That's this read's findings. Open Deep Signal to work the attribute tool yourself.
            </div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--spectral-violet)" }}>
              Open Deep Signal ›
            </div>
          </button>
        )}
      </div>

      {(views.length > 1 || linkable) && (
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "10px" }}>
          {!lastView && (
            <button
              type="button"
              onClick={() => setView(index + 1)}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                font: "inherit", fontSize: "12px", fontWeight: 500,
                color: "var(--spectral-violet)",
              }}
            >
              Show me more
            </button>
          )}
          {linkable && (
            <button
              type="button"
              onClick={onSeeAll}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                font: "inherit", fontSize: "12px", fontWeight: 500,
                color: "var(--muted-foreground)",
              }}
            >
              See all attributes ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}
